import { useState, useRef, useEffect } from 'react';
import React from 'react';
import type { PropertyMessage } from '@/components/ai/types';

// LocalStorage key helpers (duplicated from AIPropertiesChat per PR1 rules)
const getAgentThreadKey = (projectId: string): string =>
  `terramatrix_agent_thread_${projectId}`;

const getUserAgentThreadKey = (projectId: string): string =>
  `terramatrix_user_agent_thread_${projectId}`;

const getVisualEditorThreadKey = (projectId: string): string =>
  `terramatrix_visual_editor_thread_${projectId}`;

export interface UseConversationPersistenceDeps {
  tokens: { accessToken?: string } | null;
  projectId: string | undefined;
  initializeStore: (projectId?: string) => Promise<void>;
  loadConversations: (projectId?: string) => void;
  resumeActiveConversation: (projectId?: string) => void;
  clearActiveConversation: () => void;
  syncMessages: () => Promise<void>;
  fetchUserCommunity: () => void;
  activeConversationId: string | null;
  storeMessages: PropertyMessage[];
  messages: PropertyMessage[];
  setMessages: React.Dispatch<React.SetStateAction<PropertyMessage[]>>;
}

export interface UseConversationPersistenceReturn {
  storeInitialized: boolean;
  agentThreadIdRef: React.MutableRefObject<string | null>;
  agentVisualThreadIdRef: React.MutableRefObject<string | null>;
  userAgentThreadIdRef: React.MutableRefObject<string | null>;
  currentProjectIdRef: React.MutableRefObject<string | null>;
  resetAgentThread: () => void;
}

export function useConversationPersistence(deps: UseConversationPersistenceDeps): UseConversationPersistenceReturn {
  const {
    tokens,
    projectId,
    initializeStore,
    loadConversations,
    resumeActiveConversation,
    clearActiveConversation,
    syncMessages,
    fetchUserCommunity,
    activeConversationId,
    storeMessages,
    messages,
    setMessages,
  } = deps;

  // Track if we've initialized the store
  const [storeInitialized, setStoreInitialized] = useState(false);

  // Track current project for detecting project changes
  const currentProjectIdRef = useRef<string | null>(null);

  const agentThreadIdRef = useRef<string | null>(null);
  const agentVisualThreadIdRef = useRef<string | null>(null);
  const userAgentThreadIdRef = useRef<string | null>(null);

  // Phase 3: Agent thread reset helper
  const resetAgentThread = (): void => {
    agentThreadIdRef.current = null;
    if (projectId) {
      localStorage.removeItem(getAgentThreadKey(projectId));
    }
  };

  // Initialize store on mount when authenticated
  useEffect(() => {
    if (!storeInitialized && tokens?.accessToken) {
      currentProjectIdRef.current = projectId || null;
      initializeStore(projectId).then(() => {
        setStoreInitialized(true);
      }).catch((err) => {
        console.error('❌ [AI Chat] Failed to initialize store:', err);
        // Continue without persistence - local state will work
        setStoreInitialized(true);
      });
    }
  }, [storeInitialized, tokens?.accessToken, initializeStore, projectId]);

  // Phase 1: Fetch user community for agent routing
  useEffect(() => {
    fetchUserCommunity();
  }, [fetchUserCommunity]);

  // Reload conversations when project changes
  useEffect(() => {
    if (storeInitialized && projectId && projectId !== currentProjectIdRef.current) {
      currentProjectIdRef.current = projectId;
      // Clear current conversation first, then reload for new project
      clearActiveConversation();
      setMessages([]); // Also clear local messages state
      agentThreadIdRef.current = null; // Phase 3: Clear in-memory thread on project switch
      userAgentThreadIdRef.current = null;
      loadConversations(projectId);
      resumeActiveConversation(projectId);
    }
  }, [projectId, storeInitialized, loadConversations, resumeActiveConversation, clearActiveConversation]);

  // Phase 3: Restore agent thread ID from localStorage on mount/project change
  useEffect(() => {
    if (!projectId || !storeInitialized) return;
    const key = getAgentThreadKey(projectId);
    const savedThreadId = localStorage.getItem(key);
    if (savedThreadId) {
      agentThreadIdRef.current = savedThreadId;
    } else {
      agentThreadIdRef.current = null;
    }
  }, [projectId, storeInitialized]);

  useEffect(() => {
    if (!projectId || !storeInitialized) return;
    const key = getUserAgentThreadKey(projectId);
    const savedThreadId = localStorage.getItem(key);
    if (savedThreadId) {
      userAgentThreadIdRef.current = savedThreadId;
    } else {
      userAgentThreadIdRef.current = null;
    }
  }, [projectId, storeInitialized]);

  // Restore visual editor thread ID from localStorage on mount/project change
  useEffect(() => {
    if (!projectId || !storeInitialized) return;
    const key = getVisualEditorThreadKey(projectId);
    const savedThreadId = localStorage.getItem(key);
    if (savedThreadId) {
      agentVisualThreadIdRef.current = savedThreadId;
    } else {
      agentVisualThreadIdRef.current = null;
    }
  }, [projectId, storeInitialized]);

  // Sync messages from store when conversation changes
  useEffect(() => {
    if (activeConversationId && storeInitialized) {
      // Deduplicate messages by ID (keep first occurrence to preserve order)
      // This handles cases where duplicate messages were synced to the server
      const seenIds = new Set<string>();
      const deduplicatedMessages = storeMessages.filter(msg => {
        if (seenIds.has(msg.id)) {
          return false;
        }
        seenIds.add(msg.id);
        return true;
      });

      // Convert store messages to PropertyMessage format (or empty array for new conversation)
      const convertedMessages: PropertyMessage[] = deduplicatedMessages.map(msg => ({
        id: msg.id,
        content: msg.content,
        role: msg.role,
        timestamp: msg.timestamp,
        isStreaming: msg.isStreaming,
        context: msg.context,
        componentData: msg.componentData,
        layoutData: msg.layoutData,
        applied: msg.applied,
        propertyChanges: msg.propertyChanges,
        metadata: msg.metadata,
      }));
      setMessages(convertedMessages);
    }
  }, [activeConversationId, storeMessages, storeInitialized]);

  // Auto-save messages to server periodically (debounced)
  useEffect(() => {
    if (!storeInitialized || !activeConversationId || !tokens?.accessToken) return;

    const saveTimer = setTimeout(() => {
      syncMessages().catch(err => {
        console.warn('[AI Chat] Failed to sync messages:', err);
      });
    }, 2000); // Save every 2 seconds if there are pending messages

    return () => clearTimeout(saveTimer);
  }, [messages, storeInitialized, activeConversationId, tokens?.accessToken, syncMessages]);

  return {
    storeInitialized,
    agentThreadIdRef,
    agentVisualThreadIdRef,
    userAgentThreadIdRef,
    currentProjectIdRef,
    resetAgentThread,
  };
}
